import { Avatar, Box, Switch, TextField, Typography } from "@mui/material"
import { FocusEventHandler, SetStateAction, useEffect, useState } from "react"
import { GenerateToken } from "./GenerateToken";
import { useProducts } from "../hooks/useProducts";
import { IProduct } from "../types/types";
import { ref, update } from "firebase/database";
import { realtimeDb } from "../utils/firebase";
import { normalizePolish } from "../utils/normlizePolish";
import { BottomSheet } from "./BottomSheet";

export const Settings = ({ user, setUser }: {
  user: { user: string, password: string }, setUser: (user: SetStateAction<{
    user: string;
    password: string;
  }>) => void
}) => {
  const { products } = useProducts();
  const [loading, setLoading] = useState('');
  const [editingProduct, setEditingProduct] = useState<IProduct[]>();

  const handleUserChange: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
    setUser((prev) => ({ ...prev, user: e.target.value }));
  }
  const handlePasswordChange: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
    setUser((prev) => ({ ...prev, password: e.target.value }));
  }

  const handleChangeProductId = async (product: IProduct) => {
    setLoading(product.name);

    if (product.productId) {
      try {
        const updatedProduct = { ...product, productId: null }
        await update(ref(realtimeDb, `products/${product.id}`), updatedProduct);
      } catch {
        console.error(`Nie udało się zmienić ID produktu: ${product.id}`);
      } finally {
        setLoading('')
      }
    } else {
      try {
        const result = await fetch("https://frisco-meal-order-api.vercel.app/api/products", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ search: product.name })
        });
        const data: IProduct[] = await result.json();
        setEditingProduct(data.map((p) => ({ ...p, id: normalizePolish(p.name) })));
      } catch {
        console.error("Nie udało się zmienić ID produktu");
      }
    }
  }

  const handleAddProductId = async (product: IProduct) => {
    try {
      await update(ref(realtimeDb, `products/${product.id}`), { productId: product.productId })
      setLoading('');
      setEditingProduct(undefined);
    } catch {
      console.error("Nie udało się zmienić ID produktu");
    }
  };

  useEffect(() => {
    localStorage.setItem("user", JSON.stringify(user));
  }, [user]);

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div style={{ marginBottom: 8, fontWeight: "bold" }}>Dane do logowania</div>
      <TextField type="email" label="Adres e-mail" variant="outlined" value={user.user} onChange={handleUserChange} />
      <TextField type="password" label="Hasło" variant="outlined" value={user.password} onChange={handlePasswordChange} />
      <GenerateToken />

      <div style={{ marginTop: 16, fontWeight: "bold" }}>Edycja produktów</div>
      <ul style={{ listStyle: "none", paddingLeft: 0, height: "100%", maxHeight: 290, overflow: "auto", paddingBottom: 46, marginTop: 16 }}>
        {products.map((product) => {
          return (
            <li key={product.name} style={{ marginBottom: 4 }}>
              <div style={{ display: "flex", gap: 4, alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                  {product.imageUrl && <Avatar slotProps={{ root: { style: { background: "#fff" } }, img: { style: { objectFit: "contain" } } }} alt={product.name} src={product.imageUrl} variant="rounded" />}
                  <Typography sx={{ textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden", maxWidth: 235 }}>{product.name}</Typography>
                </div>
                {loading === product.name ? (
                  <div style={{ width: 40, height: 40 }}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"><circle fill="#1f8038" stroke="#1f8038" stroke-width="2" r="15" cx="35" cy="100"><animate attributeName="cx" calcMode="spline" dur="2" values="35;165;165;35;35" keySplines="0 .1 .5 1;0 .1 .5 1;0 .1 .5 1;0 .1 .5 1" repeatCount="indefinite" begin="0"></animate></circle><circle fill="#1f8038" stroke="#1f8038" stroke-width="2" opacity=".8" r="15" cx="35" cy="100"><animate attributeName="cx" calcMode="spline" dur="2" values="35;165;165;35;35" keySplines="0 .1 .5 1;0 .1 .5 1;0 .1 .5 1;0 .1 .5 1" repeatCount="indefinite" begin="0.05"></animate></circle><circle fill="#1f8038" stroke="#1f8038" stroke-width="2" opacity=".6" r="15" cx="35" cy="100"><animate attributeName="cx" calcMode="spline" dur="2" values="35;165;165;35;35" keySplines="0 .1 .5 1;0 .1 .5 1;0 .1 .5 1;0 .1 .5 1" repeatCount="indefinite" begin=".1"></animate></circle><circle fill="#1f8038" stroke="#1f8038" stroke-width="2" opacity=".4" r="15" cx="35" cy="100"><animate attributeName="cx" calcMode="spline" dur="2" values="35;165;165;35;35" keySplines="0 .1 .5 1;0 .1 .5 1;0 .1 .5 1;0 .1 .5 1" repeatCount="indefinite" begin=".15"></animate></circle><circle fill="#1f8038" stroke="#1f8038" stroke-width="2" opacity=".2" r="15" cx="35" cy="100"><animate attributeName="cx" calcMode="spline" dur="2" values="35;165;165;35;35" keySplines="0 .1 .5 1;0 .1 .5 1;0 .1 .5 1;0 .1 .5 1" repeatCount="indefinite" begin=".2"></animate></circle></svg>
                  </div>
                ) : (
                  <Switch
                    checked={!!product.productId}
                    onChange={() => handleChangeProductId(product)}
                  />
                )}
              </div>
            </li>
          )
        })}
      </ul>
      <BottomSheet isOpen={!!editingProduct} handleClose={() => setEditingProduct(undefined)} handleOpen={() => setEditingProduct(editingProduct)}>
        <ul style={{ listStyle: "none", paddingLeft: 0, height: "100%", overflow: "auto", paddingBottom: 46, marginTop: 16 }}>
            {editingProduct?.map((product) => {
              return (
                <li key={product.name} onClick={() => handleAddProductId(product)} style={{ marginBottom: 4 }}>
                  <div style={{ display: "flex", gap: 4, alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                      {product.imageUrl && <Avatar slotProps={{ root: { style: { background: "#fff" } }, img: { style: { objectFit: "contain" } } }} alt={product.name} src={product.imageUrl} variant="rounded" />}
                      <Typography sx={{ textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden", maxWidth: 235 }}>{product.name}</Typography>
                    </div>
                    <Typography>{product.capacity} {product.unit}</Typography>
                  </div>
                </li>
              )
            })}
          </ul>
      </BottomSheet>
    </Box>
  )
}
